/* R796 Battlegrounds page: design tokens (contract §7.6).
   The values are Obsidian Quiet round 2, the look Jan approved on 2026-09-22
   (scratchpad bgdemo/q2work/lib.cjs and q2gen/lib.cjs are the source boards).
   The contract's key names are kept; the look added a few keys of its own
   (surface3, dock, line, line2, edge, faint, goldInk, corrupt..., crystal,
   ember, rarity) and the section's CSS reads every one of them through
   --bg-<group>-<key> with the same value as its fallback, so the first frame
   (before this module runs) already paints in the approved colours.
   applyTokens(root) writes each value as a CSS custom property
   --bg-<group>-<key> (arrays as --bg-<group>-<key>-<i>). */

export const TOKENS = Object.freeze({
  color: Object.freeze({
    bg: '#0B0C10', surface: '#12141A', surface2: '#171A21', surface3: '#1C1F27', dock: '#0E0F14',
    ink: '#EEF1F6', inkDim: '#9AA3B2', faint: '#7F8898',
    line: 'rgba(201,208,218,.14)', line2: 'rgba(201,208,218,.22)', edge: 'rgba(255,255,255,.05)',
    gold: '#D9B26A', goldHi: '#E6C88C', goldInk: '#17120A', goldInk2: '#4A3B20',
    danger: '#C8102E', heal: '#74D38A', crit: '#C8102E', critRim: '#EF3345',
    burst: Object.freeze(['#F2243A', '#D6122E', '#A30C24']),
    phys: '#F27A82', magic: '#B9A6FF', true: '#FFFFFF', armor: '#A3B5C8', ward: '#7FCFD6', miss: '#8B8594', dodge: '#B9C6D8',
    corrupt: '#7B61D9', corruptBg: '#3B2B78', corruptInk: '#E4DBFF',
    crystal: '#CF6F8C', crystalText: '#E7A7BA', ember: '#E0925A', ivory: '#F1E4C8',
    /* the battle ladder (RARITY_COLORS), Common to Mythic */
    tier: Object.freeze(['#8A8090', '#6BCB77', '#5B8EC4', '#B46BFF', '#E8C674', '#EF94FF'])
  }),
  space: Object.freeze({ 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 7: 32, 8: 48 }),
  radius: Object.freeze({ s: 8, m: 12, l: 16, pill: 999 }),
  font: Object.freeze({
    display: "'EB Garamond', Georgia, 'Times New Roman', serif",
    body: "'Geist', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    num: "'Geist', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
  }),
  size: Object.freeze({ tap: 44, topbar: 64, tabbar: 76, portraitMin: 30, portraitMax: 280 }),
  motion: Object.freeze({ tabMs: 220, sheetMs: 280, popMs: 220, hitFlashMs: 120, reduced: false }),
  z: Object.freeze({ hud: 10, sheet: 40, toast: 60, fx: 30 })
});

const PX = { space: 1, radius: 1, size: 1 };

function reducedMotion() {
  try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (_) { return false; }
}

function cssValue(group, key, value) {
  if (typeof value === 'number') {
    if (PX[group]) return value + 'px';
    if (group === 'motion') return value + 'ms';
    return String(value);
  }
  if (typeof value === 'boolean') return value ? '1' : '0';
  return String(value);
}

/* Returns the flat {property: value} map it wrote, so a proof can read it back.
   Reduced motion is read once here: the token keeps false and the written
   --bg-motion-reduced says what this device asked for. */
export function applyTokens(root, options) {
  const target = root || (typeof document !== 'undefined' ? document.documentElement : null);
  const written = {};
  const reduced = options && typeof options.reduced === 'boolean' ? options.reduced : reducedMotion();
  for (const group of Object.keys(TOKENS)) {
    const values = TOKENS[group];
    for (const key of Object.keys(values)) {
      const value = group === 'motion' && key === 'reduced' ? reduced : values[key];
      if (Array.isArray(value)) value.forEach((v, i) => { written['--bg-' + group + '-' + key + '-' + i] = cssValue(group, key, v); });
      else written['--bg-' + group + '-' + key] = cssValue(group, key, value);
    }
  }
  if (target && target.style) {
    for (const name of Object.keys(written)) target.style.setProperty(name, written[name]);
  }
  return written;
}
